import { openInNewTab } from "@/utils/open-in-new-tab";
import { type AccountingSheetReportType } from "@/apis/dsd/route";
import { NewChip } from "@/components/common/new-chip";
import { TooltipWrapper } from "@/components/common/tooltip";
import {
  CONSOLIDATION_ITEMS,
  CURRENT_YEAR_KST,
  PERIOD_ITEMS,
  REPORT_FORMAT_ITEMS,
  YEAR_ITEMS,
  type AccountingSheetPeriod as Period,
  type ConsolidationType,
} from "@/constants/accounting-sheet";
import { useConvertAccountingSheet } from "@/hooks/mutations/use-dsd-convert";
import { useAcceptedFileSelect } from "@/hooks/use-accepted-file-select";
import { cn } from "@/lib/utils";
import Button from "@/toggle-ui-kit/button";
import { SegmentControl } from "@/toggle-ui-kit/segment-control";
import { getFileExtension } from "@/utils/file-accept";
import {
  BookOpen,
  FileSpreadsheet,
  FileText,
  Loader2,
  Repeat2,
  X,
} from "lucide-react";
import { useCallback, useRef, useState } from "react";

interface AccountingSheetDsdConvertPageProps {
  className?: string;
}

const ACCEPTED_EXTENSIONS = [".xlsx", ".xlsm"];
const ACCEPT = ACCEPTED_EXTENSIONS.join(",");

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** 확장자에 따라 아이콘과 색상을 반환 */
function getFileTypeInfo(filename: string) {
  const ext = getFileExtension(filename);
  if (ext === ".xlsx" || ext === ".xlsm") {
    return {
      label: ext.toUpperCase().substring(1), // "XLSX" 또는 "XLSM"
      icon: FileSpreadsheet,
      bgColor: "bg-green-50",
      textColor: "text-green-700",
      iconColor: "text-green-500",
      borderColor: "border-green-200",
    };
  }
  return {
    label: "DSD",
    icon: FileText,
    bgColor: "bg-blue-50",
    textColor: "text-blue-700",
    iconColor: "text-blue-500",
    borderColor: "border-blue-200",
  };
}

export const AccountingSheetDsdConvertPage = ({
  className,
}: AccountingSheetDsdConvertPageProps) => {
  const [file, setFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [reportFormat, setReportFormat] =
    useState<AccountingSheetReportType>("audit");
  const [period, setPeriod] = useState<Period>("quarterly_1");
  const [year, setYear] = useState<string>(String(CURRENT_YEAR_KST));
  const [consolidation, setConsolidation] =
    useState<ConsolidationType>("consolidated");
  const inputRef = useRef<HTMLInputElement>(null);
  const dragCounterRef = useRef(0);

  const { mutate, isPending } = useConvertAccountingSheet();

  const clearFile = useCallback(() => {
    setFile(null);
    if (inputRef.current) inputRef.current.value = "";
  }, []);

  const handleFileSelect = useAcceptedFileSelect({
    accept: ACCEPT,
    inputRef,
    onFileSelect: setFile,
  });

  // 드래그 앤 드롭 핸들러
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current += 1;
    if (dragCounterRef.current === 1) setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current -= 1;
    if (dragCounterRef.current === 0) setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounterRef.current = 0;
      setIsDragging(false);

      const droppedFile = e.dataTransfer.files?.[0] ?? null;
      handleFileSelect(droppedFile);
    },
    [handleFileSelect],
  );

  const handleClickUploadZone = () => {
    inputRef.current?.click();
  };

  const fileTypeInfo = file ? getFileTypeInfo(file.name) : null;

  return (
    <div className={cn("flex h-full w-full", className)}>
      <div className="flex-1 flex items-center justify-center bg-white transition-all duration-300">
        <div className="flex flex-col max-w-[840px] w-full gap-8 justify-center items-start px-6">
          {/* 헤더 섹션 */}
          <div className="flex flex-col gap-2 w-full">
            <div className="flex items-center gap-2">
              <h1 className="title-2-bold text-primary">정산표 DSD 변환</h1>
              <NewChip color="pink" size="large" />
            </div>
            <p className="body-1-medium text-neutral-500">
              정산표 파일을 DSD 파일로 변환할 수 있습니다.
            </p>
          </div>

          {/* 파일 업로드 영역 */}
          <div className="flex flex-col gap-3 w-full">
            {/* 숨겨진 파일 인풋 */}
            <input
              ref={inputRef}
              type="file"
              accept={ACCEPT}
              className="hidden"
              onChange={(e) => handleFileSelect(e.target.files?.[0] ?? null)}
            />

            {!file ? (
              /* 드래그 앤 드롭 업로드 존 */
              <button
                type="button"
                onClick={handleClickUploadZone}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                className={cn(
                  "group relative flex flex-col items-center justify-center gap-3 w-full py-12 px-6",
                  "rounded-xl border-2 border-dashed cursor-pointer",
                  "transition-all duration-200 ease-in-out",
                  isDragging
                    ? "border-blue-400 bg-blue-50/60 scale-[1.01]"
                    : "border-neutral-200 bg-neutral-50/50 hover:border-blue-300 hover:bg-blue-50/30",
                )}
              >
                <div className="flex flex-col items-center gap-1">
                  <p
                    className={cn(
                      "body-2-medium transition-colors duration-200",
                      isDragging ? "text-blue-600" : "text-neutral-700",
                    )}
                  >
                    {isDragging
                      ? "여기에 파일을 놓아주세요"
                      : "클릭하거나 파일을 드래그하여 업로드"}
                  </p>
                  <p className="caption-1-regular text-neutral-400">
                    XLSX 파일만 지원됩니다
                  </p>
                </div>
              </button>
            ) : (
              /* 파일 선택 완료 상태 */
              <div
                className={cn(
                  "flex items-center gap-4 w-full p-4 rounded-xl border transition-all duration-200",
                  fileTypeInfo?.borderColor,
                  fileTypeInfo?.bgColor,
                  "bg-opacity-40",
                )}
              >
                {/* 파일 아이콘 */}
                <div
                  className={cn(
                    "flex items-center justify-center w-10 h-10 rounded-lg shrink-0",
                    fileTypeInfo?.bgColor,
                  )}
                >
                  {fileTypeInfo?.icon && (
                    <fileTypeInfo.icon
                      className={cn("w-5 h-5", fileTypeInfo.iconColor)}
                    />
                  )}
                </div>

                {/* 파일 정보 */}
                <div className="flex flex-col gap-0.5 min-w-0 flex-1">
                  <p className="body-2-medium text-neutral-800 truncate">
                    {file.name}
                  </p>
                  <div className="flex items-center gap-2">
                    <span
                      className={cn(
                        "caption-2-semibold px-1.5 py-0.5 rounded",
                        fileTypeInfo?.bgColor,
                        fileTypeInfo?.textColor,
                      )}
                    >
                      {fileTypeInfo?.label}
                    </span>
                    <span className="caption-2-regular text-neutral-400">
                      {formatFileSize(file.size)}
                    </span>
                  </div>
                </div>

                {/* 파일 제거 버튼 */}
                <button
                  type="button"
                  onClick={clearFile}
                  className="flex items-center justify-center w-8 h-8 rounded-lg shrink-0 text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 transition-colors duration-150"
                  aria-label="파일 제거"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
          {/* 옵션 선택 섹션 */}
          <div className="flex flex-col gap-4 w-full">
            <div className="flex flex-col gap-2">
              <p className="label-1-normal-medium text-label-normal">
                감사 여부
              </p>
              <SegmentControl
                items={REPORT_FORMAT_ITEMS}
                value={reportFormat}
                onChange={setReportFormat}
                size="base"
              />
            </div>
            <div className="flex flex-col gap-2">
              <p className="label-1-normal-medium text-label-normal">연도</p>
              <SegmentControl
                items={YEAR_ITEMS}
                value={year}
                onChange={setYear}
                size="base"
              />
            </div>
            <div className="flex flex-col gap-2">
              <p className="label-1-normal-medium text-label-normal">
                보고서 기간
              </p>
              <SegmentControl
                items={PERIOD_ITEMS.map((item) => ({
                  ...item,
                  disabled: reportFormat === "pre_audit",
                }))}
                // pre_audit일 때는 어느 item과도 매치되지 않는 sentinel을 넘겨
                // 검은색 선택 배경이 뜨지 않도록 한다(period state 자체는 유지).
                value={reportFormat === "pre_audit" ? ("" as Period) : period}
                onChange={setPeriod}
                size="base"
              />
            </div>
            <div className="flex flex-col gap-2">
              <p className="label-1-normal-medium text-label-normal">
                보고서 종류
              </p>
              <SegmentControl
                items={CONSOLIDATION_ITEMS}
                value={consolidation}
                onChange={setConsolidation}
                size="base"
              />
            </div>
          </div>
          {/* 파일 변환 다운로드 버튼 */}
          <div className="flex items-end justify-end gap-2 w-full">
            <Button
              type="button"
              variant="outlined"
              size={36}
              leftIcon={<BookOpen className="w-4 h-4" />}
              onClick={() =>
                openInNewTab(
                  "https://docs.google.com/presentation/d/1d4jLMuOd2XA2vr0wu-m8yOUPnJGDNohJPUfXGgW4f_A/edit?slide=id.g3d693cb7408_0_0#slide=id.g3d693cb7408_0_0",
                )
              }
            >
              사용 설명서
            </Button>
            <TooltipWrapper
              label="클릭하여 다운로드 받을 수 있습니다."
              side="left"
              arrowed
              styles={{
                contentStyle: "border-none p-0",
                bgStyle: "bg-neutral-900 px-1.5 py-1",
                textStyle: "label-2-medium text-white",
                arrowStyle: "fill-neutral-900",
              }}
            >
              <Button
                variant="solid"
                size={36}
                leftIcon={
                  !isPending ? (
                    <Repeat2 className="w-4 h-4" />
                  ) : (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  )
                }
                disabled={!file || isPending}
                onClick={() =>
                  file &&
                  mutate(
                    {
                      file,
                      params: {
                        reportType: reportFormat,
                        scope: consolidation,
                        period: reportFormat === "audit" ? period : undefined,
                        year: Number(year),
                      },
                    },
                    { onSuccess: clearFile },
                  )
                }
              >
                {isPending ? "변환 중..." : "변환하기"}
              </Button>
            </TooltipWrapper>
          </div>
        </div>
      </div>
    </div>
  );
};
